<template>
  <v-card flat>
    <v-card-title class="px-0 pb-0">
      <v-icon color="error" class="mr-2" v-if="!valid">mdi-close-circle</v-icon>
      Upload Scans
    </v-card-title>

    <v-card-text v-if="retrying" class="mt-5">
      <v-alert color="warning" dark dense>
        <v-icon class="mr-2">mdi-alert-circle</v-icon>
        Your previous scan validation failed.
      </v-alert>
      <p class="ml-5">
        Please supply an additional set of scans to retry validation.
      </p>
      <v-data-table
        v-if="retrying"
        :headers="[
          { text: 'Attempt #', value: 'attempt' },
          { text: 'Scan 1', value: 'scan1' },
          { text: 'Scan 2', value: 'scan2' }
        ]"
        :items="uploadedScans"
        disable-sort
        hide-default-footer
      />
    </v-card-text>

    <v-card-text
      v-if="uploadedScans.length === 3 && validationPass === false"
      class="mt-5"
    >
      <v-alert color="error" dark dense>
        <v-icon class="mr-2">mdi-alert-circle</v-icon>
        Maximum retry count reached - you will need to restart this order with
        new gauge scans.
      </v-alert>
    </v-card-text>

    <v-row>
      <v-col cols="6" class="px-0">
        <NewOrderScanUploader
          v-model="leftToRightScan"
          :disabled="!!bypass"
          categoryTitle="Left to Right"
          :skip-validation="skipValidation"
        />
        <v-chip
          v-if="leftToRightScan"
          small
          close
          class="rounded-10 mx-4"
          :disabled="!!bypass"
          @click:close="leftToRightScan = null"
        >
          <v-icon small>mdi-paperclip</v-icon>
          <span class="text-truncate">
            {{ leftToRightScan.name }}
          </span>
        </v-chip>
      </v-col>
      <v-col cols="6" class="px-0">
        <NewOrderScanUploader
          v-model="rightToLeftScan"
          :disabled="!!bypass"
          categoryTitle="Right to Left"
          :skip-validation="skipValidation"
        />
        <v-chip
          v-if="rightToLeftScan"
          small
          close
          class="rounded-10 mx-4"
          :disabled="!!bypass"
          @click:close="rightToLeftScan = null"
        >
          <v-icon small>mdi-paperclip</v-icon>
          <span class="text-truncate">
            {{ rightToLeftScan.name }}
          </span>
        </v-chip>
      </v-col>
      <v-col cols="12" class="pt-0 ms-2">
        <v-checkbox
          v-model="narrowScanBodiesPresent"
          label="Include Narrow Scan Bodies"
          dense
          class="mt-0 pt-0"
        />
      </v-col>
    </v-row>

    <v-card-title class="px-0">
      <v-icon v-if="!valid && !validationPass" color="error" class="mr-2"
        >mdi-close-circle</v-icon
      >
      Verify Scans
    </v-card-title>

    <v-card-text class="px-0">
      <span v-if="!validateScansResponse" :class="valid ? null : 'error--text'">
        Scans have not been verified.
      </span>
      <template v-if="validateScansResponse">
        <span v-if="!validationPass" :class="valid ? null : 'error--text'">
          <v-icon color="error" class="mr-1">mdi-close-circle</v-icon>
          Scans have failed verification.
        </span>
        <span v-if="validationPass">
          <v-icon v-if="!validationReconsider" color="success" class="mr-1">
            mdi-check-circle
          </v-icon>
          <v-icon v-if="validationReconsider" color="warning" class="mr-1">
            mdi-alert-circle
          </v-icon>
          <span v-if="!validationReconsider">
            Scans have been successfully verified.
          </span>
          <span v-if="validationReconsider">
            Scans have been verified, consider rescanning.
          </span>
        </span>
      </template>
    </v-card-text>

    <div class="d-flex">
      <v-btn
        class="btn-primary"
        @click="toggleValidateScans"
        :disabled="
          !this.leftToRightScan || !this.rightToLeftScan || loading || ncLoading
        "
      >
        <span v-if="!bypass"> Verify Scans </span>
        <span v-if="bypass"> Scan Verification </span>
      </v-btn>
      <v-btn
        v-if="validationPass === false && uploadedScans.length < 3"
        class="ml-3 elevation-0 rounded-10"
        @click="setRetrying(true)"
        :disabled="loading || ncLoading"
      >
        Retry
      </v-btn>
      <v-btn
        v-if="validationPass === false && uploadedScans.length === 3"
        class="ml-3 elevation-0 rounded-10"
        @click="restart"
        :disabled="loading || ncLoading"
      >
        Restart
      </v-btn>
      <v-scale-transition origin="center center">
        <div class="flex-grow-1 ml-3 mr-1" v-if="ncLoading">
          <v-progress-linear
            rounded
            indeterminate
            color="primary"
            height="36"
            class="rounded-10"
            background-opacity="0.6"
          >
            <span
              class="text-body-2 font-weight-medium white--text loading-pulse"
            >
              PROCESSING...
            </span>
          </v-progress-linear>
        </div>
      </v-scale-transition>
    </div>

    <v-dialog
      v-model="validateScansDialog"
      width="70vw"
      no-click-animation
      :persistent="!validationPass"
    >
      <NewOrderScanValidator
        v-model="validateScansResponse"
        :validateScansDialog="validateScansDialog"
        :retrying="retrying"
        :retryCount="retryCount"
        :ncSessionId="ncSessionId"
        :addScansResponse="addScansResponse"
        :uploadedScans="uploadedScans"
        :validationPass="validationPass"
        :validationReconsider="validationReconsider"
        :immediate="immediate"
        :narrowScanBodiesPresent="narrowScanBodiesPresent"
        @close="validateScansDialog = false"
        @restart="restart"
        @error="setError"
        @retrying="setRetrying"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"
import apiClient from "@/lib/ApiClient"
import NewOrderScanUploader from "./NewOrderScanUploadAndValidate/NewOrderScanUploader.vue"
import NewOrderScanValidator from "./NewOrderScanUploadAndValidate/NewOrderScanValidator.vue"

export default {
  name: "NewOrderScanUploadAndValidate",

  props: [
    "dentate",
    "leftToRight",
    "rightToLeft",
    "leftToRightUpload",
    "rightToLeftUpload",
    "validateScansRes",
    "sessionId",
    "immediate",
    "loading",
    "skipValidation"
  ],

  components: {
    NewOrderScanUploader,
    NewOrderScanValidator
  },

  inject: ["form"],

  data() {
    return {
      ncLoading: false,
      retrying: false,
      validateScansDialog: false,
      retryCount: 0,
      uploadedScans: [],
      addScansResponse: null,
      narrowScanBodiesPresent: false,

      valid: true
    }
  },

  created() {
    this.form.register(this)
  },

  computed: {
    ...mapGetters(["token"]),

    ncSessionId: {
      get() {
        return this.sessionId
      },
      set(value) {
        this.$emit("update", `${this.dentate}NcSessionId`, value)
      }
    },

    leftToRightScan: {
      get() {
        return this.leftToRight
      },
      set(value) {
        this.$emit("update", `${this.dentate}LeftToRightScan`, value)
      }
    },

    rightToLeftScan: {
      get() {
        return this.rightToLeft
      },
      set(value) {
        this.$emit("update", `${this.dentate}RightToLeftScan`, value)
      }
    },

    leftToRightScanUpload: {
      get() {
        return this.leftToRightUpload
      },
      set(value) {
        this.$emit("update", `${this.dentate}LeftToRightUpload`, value)
      }
    },

    rightToLeftScanUpload: {
      get() {
        return this.rightToLeftUpload
      },
      set(value) {
        this.$emit("update", `${this.dentate}RightToLeftUpload`, value)
      }
    },

    validateScansResponse: {
      get() {
        return this.validateScansRes
      },
      set(value) {
        this.$emit("update", `${this.dentate}ValidateScansResponse`, value)
      }
    },

    validationReconsider() {
      if (!this.validateScansResponse) return
      return (
        NCFrontend.classifyValidationScore(
          this.validateScansResponse,
          this.immediate
        ) === "Consider Rescan"
      )
    },

    validationPass() {
      if (!this.validateScansResponse) return
      return (
        NCFrontend.classifyValidationScore(
          this.validateScansResponse,
          this.immediate
        ) !== "Fail"
      )
    },

    bypass() {
      return (
        (!this.retrying && this.uploadedScans.length) ||
        this.uploadedScans.length === this.retryCount + 1
      )
    }
  },

  methods: {
    ...mapActions(["setErrorMessage"]),

    async toggleValidateScans() {
      if (!this.leftToRightScan || !this.rightToLeftScan) return
      this.ncLoading = true
      if (!this.ncSessionId) {
        // Error handling?
        try {
          const response = await NCFrontend.ApiClient.sessionCreate(
            `${apiClient.instanceUrl}/${apiClient.tenantUid}/validation`,
            this.$axios.defaults.headers.Authorization
          )
          this.ncSessionId = response.sessionId
        } catch (err) {
          this.setErrorMessage("An internal error occurred.")
          console.error(err)
          this.ncLoading = false
          return
        }
      }

      try {
        const uploadedFiles = await apiClient.oft.uploadFiles([
          this.leftToRightScan,
          this.rightToLeftScan
        ])
        await apiClient.oft.getFileUrls(uploadedFiles)
        this.leftToRightScanUpload = uploadedFiles.find(
          (file) => file.file.name === this.leftToRightScan.name
        )
        this.rightToLeftScanUpload = uploadedFiles.find(
          (file) => file.file.name === this.rightToLeftScan.name
        )
      } catch (err) {
        this.setErrorMessage("Failed to upload scans")
        console.error(err)
        this.ncLoading = false
        return
      }

      this.$nextTick(async () => {
        if (
          !this.uploadedScans.length ||
          (this.retrying && this.uploadedScans.length === this.retryCount)
        ) {
          try {
            this.addScansResponse = await NCFrontend.ApiClient.sessionAddScans(
              `${apiClient.instanceUrl}/${apiClient.tenantUid}/validation`,
              this.$axios.defaults.headers.Authorization,
              {
                sessionId: this.ncSessionId,
                scan1FileUrl: this.leftToRightScanUpload.url,
                scan1FileOftUid: this.leftToRightScanUpload.oftUid,
                scan2FileUrl: this.rightToLeftScanUpload.url,
                scan2FileOftUid: this.rightToLeftScanUpload.oftUid,
                narrowScanBodiesPresent: this.narrowScanBodiesPresent,
                retrying: this.retrying
              }
            )
            this.uploadedScans.push({
              attempt: this.uploadedScans.length + 1,
              scan1: this.leftToRightScan.name,
              scan2: this.rightToLeftScan.name
            })
          } catch (err) {
            if (err instanceof NCFrontend.ApiClient.ErrorResponse) {
              switch (err.type) {
                case NCFrontend.ApiClient.ErrorType.IdenticalScanFiles:
                  this.setErrorMessage("Identical scans detected.")
                  break
                case NCFrontend.ApiClient.ErrorType.ResourceNotFound:
                  this.setErrorMessage("Failed to find session.")
                  break
                case NCFrontend.ApiClient.ErrorType.MaxValidationRetriesReached:
                  this.setErrorMessage("Max retry attempts reached.")
                  break
                case NCFrontend.ApiClient.ErrorType
                  .ScansAlreadyUsedInValidation:
                  this.leftToRightScan = null
                  this.rightToLeftScan = null
                  this.setErrorMessage(
                    "Scans have already been used in an attempt."
                  )
                  return (this.ncLoading = false)
                case NCFrontend.ApiClient.ErrorType.ServerError:
                  this.setErrorMessage("An internal server error occurred.")
                  break
              }
            } else this.setErrorMessage("An internal error occurred.")
            console.error(err)
            this.leftToRightScan = null
            this.rightToLeftScan = null
            this.ncLoading = false
            return
          }
        }

        this.ncLoading = false

        this.validateScansDialog = !this.validateScansDialog
      })
    },

    setRetrying(bool) {
      this.retrying = bool
      if (this.retrying) {
        this.retryCount++
        this.validateScansDialog = false
        this.validateScansResponse = null
        this.leftToRightScan = null
        this.rightToLeftScan = null
        this.leftToRightScanUpload = null
        this.rightToLeftScanUpload = null
      }
    },

    restart() {
      this.ncSessionId = null
      this.leftToRightScan = null
      this.rightToLeftScan = null
      this.leftToRightScanUpload = null
      this.rightToLeftScanUpload = null
      this.addScansResponse = null
      this.uploadedScans = []
      this.retrying = false
      this.validateScansResponse = null
      this.validateScansDialog = false
      this.retryCount = 0
      this.setErrorMessage(null)
    },

    setError(message) {
      this.setErrorMessage(message)
    },

    validate() {
      if (this.skipValidation) return true
      this.valid = !!this.validationPass
      return this.valid
    }
  }
}
</script>
